import { Fragment, useEffect, useMemo, useState } from "react";
import { Badge, Pagination } from "react-bootstrap";
import SearchResultListItem from "../search_result_list/SearchResultListItem";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const SearchResults = ({
    data,
    activeCategories = [],
    setShow,
    onSelect,
    searchHandler,
}) => {
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(-1);
    const [currentPage, setCurrentPage] = useState(data?.page ?? 1);

    const visibleGroups = useMemo(() => {
        const groups = data?.result ?? {};

        return Object.entries(groups).filter(([, group]) => {
            const isActive =
                activeCategories.length === 0 ||
                activeCategories.includes(group.title);

            return isActive && group.items?.length > 0;
        });
    }, [data?.result, activeCategories]);

    const navigableItems = useMemo(() => {
        return visibleGroups.flatMap(([, group]) => group.items);
    }, [visibleGroups]);

    useEffect(() => {
        setActiveIndex(navigableItems.length > 0 ? 0 : -1);
    }, [data?.term, navigableItems.length]);

    useEffect(() => {
        const keyDownHandler = event => {
            if (event.key === "Escape") {
                setShow(false);
                return;
            }

            if (navigableItems.length === 0) return;

            if (event.key === "ArrowDown") {
                event.preventDefault();

                setActiveIndex(previousIndex => {
                    if (
                        previousIndex < 0 ||
                        previousIndex >= navigableItems.length - 1
                    ) {
                        return 0;
                    }

                    return previousIndex + 1;
                });

                return;
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();

                setActiveIndex(previousIndex => {
                    if (previousIndex <= 0) {
                        return navigableItems.length - 1;
                    }

                    return previousIndex - 1;
                });

                return;
            }

            if (event.key === "Enter") {
                event.preventDefault();

                const selectedItem = navigableItems[activeIndex];

                if (selectedItem) {
                    setShow(false);
                    selectItemHandler(selectedItem);
                }
            }
        };

        window.addEventListener("keydown", keyDownHandler);

        return () => {
            window.removeEventListener("keydown", keyDownHandler);
        };
    }, [activeIndex, navigableItems, onSelect, setShow]);

    if (!data?.result || visibleGroups.length === 0) {
        return null;
    }

    let navigationIndex = 0;


    const selectItemHandler = item => {
        if (!item) return;

        setShow(false);
        onSelect?.(item);

        if (item.type === "elv") {
            return navigate(
                `/hw/dashboard/energieliefervertraege/${item.id}`
            );
        }
        else if (item.type === "building") {
            return navigate(
                `/hw/dashboard/gebaeude/anlagen/${item.id}`
            );
        }
        else {
            return navigate(
                `/hw/dashboard/energieliefervertraege/${item.elv_id}/dokumente/${item.type}/${item.id}/`
            )
        }
    };

    return (
        <ul
            className="search-results"
            role="listbox"
            aria-label="Suchergebnisse"
        >
            {visibleGroups.map(([groupKey, group]) => (
                <Fragment key={groupKey}>
                    <li
                        className="search-results__group-header"
                        role="presentation"
                    >
                        <b>{group.title}</b>

                        <Badge bg="secondary" className="ms-1">
                            {group.found}
                        </Badge>
                    </li>

                    {group.items.map(item => {
                        const currentIndex = navigationIndex++;

                        return (
                            <SearchResultListItem
                                key={`${item.type}-${item.id}`}
                                item={item}
                                active={currentIndex === activeIndex}
                                onMouseEnter={() =>
                                    setActiveIndex(currentIndex)
                                }
                                selectHandler={selectItemHandler}
                            />
                        );
                    })}
                    {group.found > 4 && group.items.length < 5 ?
                        <a 
                            onClick={e => {e.preventDefault(); searchHandler(group.title, currentPage)} }
                            href={`/mehr/${group.title}`} 
                            className="link-energo ms-2"
                        >
                            Mehr anzeigen <FaChevronDown />
                        </a> 
                        :
                        group.items.length > 4 ?
                        <div className="d-flex justify-content-center">
                            <Pagination>
                                <Pagination.First disabled={currentPage < 2} onClick={() => {setCurrentPage(1); searchHandler(group.title, 1) }} />
                                <Pagination.Prev disabled={currentPage < 2} onClick={() => {setCurrentPage(currentPage - 1); searchHandler(group.title, currentPage -1) }} />
                                <Pagination.Item active>{currentPage}</Pagination.Item>
                                <Pagination.Next disabled={(currentPage * 12) >= group.found} onClick={() => {setCurrentPage(currentPage + 1); searchHandler(group.title, currentPage +1) }} />
                                <Pagination.Last disabled={(currentPage * 12) >= group.found} onClick={() => {setCurrentPage(Math.ceil(group.found / 12)); searchHandler(group.title, Math.ceil(group.found / 12)) }} />
                            </Pagination>
                        </div>
                        :
                        null
                    }
                </Fragment>
            ))}
        </ul>
    );
};

export default SearchResults;